import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, ChevronLeft, ChevronRight, FileText, Calendar, DollarSign, Eye, MapPin, Trash2 } from "lucide-react";
import { transferPlanService } from "@/services/transferPlanService";
import { toast } from "sonner";
import { Link, useLocation } from "wouter";

interface TransferPlan {
  _id: string;
  batchName: string;
  planId: string;
  summary: {
    totalRows: number;
    matchedCount: number;
    unmatchedCount: number;
    totalAmount: number;
  };
  totalWimaxIncome: number;
  totalOrpusIncome: number;
  totalOrdaIncome: number;
  totalOrlokIncome: number;
  matchedLocation: boolean;
  createdBy?: {
    _id: string;
    callsign?: string;
    nama_lengkap?: string;
    email?: string;
  };
  createdAt: string;
  updatedAt: string;
}


export function TransferPlanListPage() {
  const [transferPlans, setTransferPlans] = React.useState<TransferPlan[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [sortBy, setSortBy] = React.useState<"createdAt" | "batchName">("createdAt");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [, setLocation] = useLocation();

  const fetchTransferPlans = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await transferPlanService.getAllTransferPlans(
        page,
        20,
        sortBy,
        sortOrder
      );
      setTransferPlans(result.data);
      setTotalPages(result.totalPages);
      setTotal(result.total);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch transfer plans";
      setError(errorMessage);
      toast.error("Failed to fetch transfer plans", {
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortOrder]);

  React.useEffect(() => {
    fetchTransferPlans();
  }, [fetchTransferPlans]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("id-ID", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSort = (field: "createdAt" | "batchName") => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const handleLocationClick = (planId: string) => {
    setLocation(`/keuangan/transfer-plans/${planId}/location`);
  };

  const handleDelete = async (planId: string, batchName: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete transfer plan "${batchName}"?\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const result = await transferPlanService.deleteTransferPlan(planId);
      toast.success("Transfer plan deleted successfully", {
        description: result.updatedPaymentsCount > 0 
          ? `${result.updatedPaymentsCount} payment${result.updatedPaymentsCount !== 1 ? "s" : ""} updated`
          : undefined,
      });
      // Refresh the list
      fetchTransferPlans();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete transfer plan";
      toast.error("Failed to delete transfer plan", {
        description: errorMessage,
      });
    }
  };

  return (
    <div className="flex-1 space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Transfer Plans</h1>
          <p className="text-muted-foreground mt-1">
            View all confirmed transfer plans
          </p>
        </div>
        <Link href="/keuangan/transfer-plan">
          <Button>Create New Transfer Plan</Button>
        </Link>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Transfer Plans History</CardTitle>
          <CardDescription>
            {total > 0 ? `${total} transfer plan${total !== 1 ? "s" : ""} found` : "No transfer plans found"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : transferPlans.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No transfer plans found</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort("batchName")}
                      >
                        Batch Name
                        {sortBy === "batchName" && (
                          <span className="ml-2">{sortOrder === "asc" ? "↑" : "↓"}</span>
                        )}
                      </TableHead>
                      <TableHead>Plan ID</TableHead>
                      <TableHead className="text-right">Total Rows</TableHead>
                      <TableHead className="text-right">Matched</TableHead>
                      <TableHead className="text-right">Unmatched</TableHead>
                      <TableHead className="text-right">Total Amount</TableHead>
                      <TableHead className="text-right">Wimax Income</TableHead>
                      <TableHead className="text-right">ORPUS Income</TableHead>
                      <TableHead className="text-right">ORDA Income</TableHead>
                      <TableHead className="text-right">ORLOK Income</TableHead>
                      <TableHead className="text-center">Matched Location</TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleSort("createdAt")}
                      >
                        Created At
                        {sortBy === "createdAt" && (
                          <span className="ml-2">{sortOrder === "asc" ? "↑" : "↓"}</span>
                        )}
                      </TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transferPlans.map((plan) => (
                      <TableRow key={plan._id}>
                        <TableCell className="font-medium font-mono">
                          <Link href={`/keuangan/transfer-plans/${plan._id}`}>
                            <Button variant="ghost" size="sm" className="gap-2">
                              {plan.batchName}
                              <Eye className="h-3 w-3" />
                            </Button>
                          </Link>
                        </TableCell>
                        <TableCell className="font-mono text-sm text-muted-foreground">
                          {plan.planId}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{plan.summary.totalRows}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium text-green-600">{plan.summary.matchedCount}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium text-orange-600">{plan.summary.unmatchedCount}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <DollarSign className="h-3 w-3 text-muted-foreground" />
                            <span className="font-medium">{formatCurrency(plan.summary.totalAmount)}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{formatCurrency(plan.totalWimaxIncome)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{formatCurrency(plan.totalOrpusIncome)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{formatCurrency(plan.totalOrdaIncome)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">{formatCurrency(plan.totalOrlokIncome)}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <button
                            onClick={() => plan.matchedLocation && handleLocationClick(plan._id)}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={!plan.matchedLocation}
                            title={plan.matchedLocation ? "Click to view location summary" : "No location data available"}
                          >
                            {plan.matchedLocation ? (
                              <span className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 cursor-pointer">
                                Yes
                              </span>
                            ) : (
                              <span className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                No
                              </span>
                            )}
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span>{formatDate(plan.createdAt)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {plan.createdBy ? (
                            <div className="text-sm">
                              <div className="font-medium">{plan.createdBy.callsign || "-"}</div>
                              <div className="text-muted-foreground">{plan.createdBy.nama_lengkap || "-"}</div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDelete(plan._id, plan.batchName)}
                            title="Delete transfer plan"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1 || loading}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages || loading}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

    </div>
  );
}

